/**
 * Utility functions for handling contact field detection, validation, and disabled states
 * Used across BasicTicketCard, FreeResponseQuestion, and other form components
 */

/**
 * Sanitizes field names by replacing non-alphanumeric characters with underscores
 * Used for creating consistent field names in forms
 * @param text - The text to sanitize
 * @returns Sanitized field name
 */
export const sanitizeFieldName = (text: string): string => {
  return text.replace(/[^a-zA-Z0-9]/g, "_")
}

/**
 * Checks if a field is an email field
 * @param questionText - The question text to check
 * @returns true if the field is an email field
 */
export const isEmailField = (questionText: string): boolean => {
  return questionText.toLowerCase().includes('email')
}

/**
 * Determines if a field should be disabled based on contact sync settings
 * Uses the backend-provided `editable` field to identify contact fields
 * @param editable - Whether the question is editable (false for First Name, Last Name, Email)
 * @param isSameAsContact - Whether "same as contact" is checked
 * @param isFirstTicket - Whether this is the first ticket
 * @returns true if the field should be disabled
 */
export const shouldDisableContactField = (
  editable: boolean,
  isSameAsContact: boolean,
  isFirstTicket: boolean
): boolean => {
  // Only disable non-editable fields (First Name, Last Name, Email) when same as contact is checked on first ticket
  return !editable && isSameAsContact && isFirstTicket
}

/**
 * Gets the maximum character length for a field
 * Emails: 1023 characters, everything else: 128 characters
 * @param questionText - The question text to check
 * @returns Maximum character length for the field
 */
export const getFieldMaxLength = (questionText: string): number => {
  return isEmailField(questionText) ? 1023 : 128
}

/**
 * Gets the validation message key for max length
 * @param questionText - The question text to check
 * @returns The translation key for the validation message
 */
export const getFieldMaxLengthMessageKey = (questionText: string): string => {
  return isEmailField(questionText) ? 'validation.email_max_length' : 'validation.input_max_length'
}

/**
 * Gets the default validation message for max length
 * @param questionText - The question text to check
 * @returns The default validation message
 */
export const getFieldMaxLengthDefaultMessage = (questionText: string): string => {
  const maxLength = getFieldMaxLength(questionText)
  return isEmailField(questionText)
    ? `Email cannot exceed ${maxLength} characters`
    : `Input cannot exceed ${maxLength} characters`
}

/**
 * Checks if a field should have email validation
 * @param questionText - The question text to check
 * @param questionType - The HTML input type of the question
 * @returns true if email validation should be applied
 */
export const shouldValidateEmail = (
  questionText: string,
  questionType?: string
): boolean => {
  return questionType === 'email' || isEmailField(questionText)
}

/**
 * Email validation pattern for React Hook Form
 */
export const EMAIL_VALIDATION_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i